import { CONDITION_DESCRIPTION } from "@/constants/app";
import { createSlice } from "@reduxjs/toolkit";

// Article Listing View Enum
export const ARTICLE_LISTING_VIEWS = {
  ITEM_CONDITION: "itemCondition",
  MARKETPLACE_SELECTION: "marketplaceSelection",
  SHOW_SCOPE_DELIVERY: "showScopeDelivery",
  EBAY_CONDITION_TEXT: "ebayConditionText",
  IMAGE_UPLOAD: "imageUpload",
  OVERVIEW: "overview",
};

// Initial state
export const articleListInitialState = {
  selectedProductPrice: {
    originalPrice: null,
    discountedPrice: null,
  },

  isEbayListingAllowed: false,
  isMiraklListingAllowed: false,

  selectedMiraklCondition: null,
  selectedMiraklDiscountProfile: null,

  selectedMiraklDiscountPercentage: null,

  // for item condition
  selectedCondition: null,

  selectedMarketplace: null,
  selectedScopeDelivery: null,
  selectedEbayCondition: {
    description: CONDITION_DESCRIPTION.NEW,
  },
  selectedDiscountProfile: null,
  selectedDiscountPercentage: null,
  // eBay aspect data
  ebayAspectData: null,
  // Overview screen editable fields
  priceProposal: {
    acceptFrom: null,
    rejectAt: null,
    enabled: false,
  },
  startTime: {
    epochTime: null, // Epoch timestamp in milliseconds
    instant: true,
  },
  ebayTitle: null,
  productQuantity: 1,
  // Shipping details
  shippingCost: null,
  handlingType: null,
  processingTime: 3,
  marketPickup: null,
  eanNumber: null,
  persistedConditionDescription: CONDITION_DESCRIPTION.NEW,
  customAssets: null,
  customAssetsProductId: null,

  // Navigation state (preserved across tab switches)
  currentView: ARTICLE_LISTING_VIEWS.ITEM_CONDITION,
  breadcrumbItems: [
    { id: ARTICLE_LISTING_VIEWS.ITEM_CONDITION, labelKey: "itemCondition" },
  ],
};

// Slice
const articleListingSlice = createSlice({
  name: "articleListing",
  initialState: articleListInitialState,
  reducers: {
    setSelectedProductPrice: (state, action) => {
      state.selectedProductPrice = action.payload;
    },

    setSelectedMiraklCondition: (state, action) => {
      state.selectedMiraklCondition = action.payload;
    },
    setSelectedMiraklDiscountProfile: (state, action) => {
      state.selectedMiraklDiscountProfile = action.payload;
    },

    setEbayListingAllowed: (state, action) => {
      state.isEbayListingAllowed = action.payload;
    },

    setMiraklListingAllowed: (state, action) => {
      state.isMiraklListingAllowed = action.payload;
    },

    setSelectedMiraklDiscountPercentage: (state, action) => {
      state.selectedMiraklDiscountPercentage = action.payload;
    },

    // Set selected condition
    setSelectedCondition: (state, action) => {
      state.selectedCondition = action.payload;
    },

    // Set selected marketplace
    setSelectedMarketplace: (state, action) => {
      state.selectedMarketplace = action.payload;
    },

    // Set selected scope of delivery
    setSelectedScopeDelivery: (state, action) => {
      state.selectedScopeDelivery = action.payload;
    },

    setSelectedEbayCondition: (state, action) => {
      state.selectedEbayCondition = action.payload;
    },
    setPersistedConditionDescription: (state, action) => {
      state.persistedConditionDescription = action.payload;
    },
    // selected discount profile

    setSelectedDiscountProfile: (state, action) => {
      state.selectedDiscountProfile = action.payload;
    },

    // Set selected discount percentage
    setSelectedDiscountPercentage: (state, action) => {
      state.selectedDiscountPercentage = action.payload;
    },

    // Set eBay aspect data
    setEbayAspectData: (state, action) => {
      state.ebayAspectData = action.payload;
    },

    // Update specific eBay aspect
    updateEbayAspect: (state, action) => {
      const { aspectName, aspectValue } = action.payload;
      if (!state.ebayAspectData) {
        state.ebayAspectData = {};
      }
      state.ebayAspectData[aspectName] = aspectValue;
    },

    // Remove eBay aspect
    removeEbayAspect: (state, action) => {
      const aspectName = action.payload;
      if (state.ebayAspectData && state.ebayAspectData[aspectName]) {
        delete state.ebayAspectData[aspectName];
      }
    },

    // Reset eBay aspect data
    resetEbayAspectData: (state) => {
      state.ebayAspectData = null;
    },

    // Set price proposal data
    setPriceProposal: (state, action) => {
      state.priceProposal = {
        ...state.priceProposal,
        ...action.payload,
      };
    },

    // Set start time data
    setStartTime: (state, action) => {
      state.startTime = {
        ...state.startTime,
        ...action.payload,
      };
    },

    // Set eBay title
    setEbayTitle: (state, action) => {
      state.ebayTitle = action.payload;
    },

    // Set product quantity
    setProductQuantity: (state, action) => {
      state.productQuantity = action.payload;
    },

    // Set shipping cost
    setShippingCost: (state, action) => {
      state.shippingCost = action.payload;
    },

    // Set handling type
    setHandlingType: (state, action) => {
      state.handlingType = action.payload;
    },

    // Set processing time
    setProcessingTime: (state, action) => {
      state.processingTime = action.payload;
    },

    // Set market pickup
    setMarketPickup: (state, action) => {
      state.marketPickup = action.payload;
    },

    // Set EAN number
    setEanNumber: (state, action) => {
      state.eanNumber = action.payload;
    },

    setCustomAssets: (state, action) => {
      if (action.payload && typeof action.payload === "object" && "productId" in action.payload) {
        state.customAssets = action.payload.assets;
        state.customAssetsProductId = action.payload.productId;
      } else {
        state.customAssets = action.payload;
      }
    },

    // Set current view
    setCurrentView: (state, action) => {
      state.currentView = action.payload;
    },

    // Set breadcrumb items
    setBreadcrumbItems: (state, action) => {
      state.breadcrumbItems = action.payload;
    },

    // Reset all state
    resetArticleListing: () => articleListInitialState,
  },
});

// Export actions
export const {
  setSelectedCondition,
  setSelectedMarketplace,
  setSelectedScopeDelivery,
  setScopeDeliveryDescription,
  setCustomConditionText,
  setCustomScopeText,
  setPriceProposal,
  setStartTime,
  setEbayTitle,
  setProductQuantity,
  setShippingCost,
  setHandlingType,
  setProcessingTime,
  setMarketPickup,
  setEanNumber,
  setCustomAssets,
  setCurrentView,
  setBreadcrumbItems,
  resetArticleListing,
  setSelectedEbayCondition,
  setSelectedDiscountProfile,
  setSelectedDiscountPercentage,
  setSelectedProductPrice,
  setEbayAspectData,
  updateEbayAspect,
  removeEbayAspect,
  resetEbayAspectData,
  setPersistedConditionDescription,
  setEbayListingAllowed,
  setMiraklListingAllowed,
  setSelectedMiraklCondition,
  setSelectedMiraklDiscountProfile,
  setSelectedMiraklDiscountPercentage
} = articleListingSlice.actions;

// Export selectors

export const selectedProductPrice = (state) =>
  state.articleListing?.selectedProductPrice;

export const selectSelectedCondition = (state) =>
  state.articleListing?.selectedCondition;
export const selectSelectedMarketplace = (state) =>
  state.articleListing?.selectedMarketplace;
export const selectSelectedScopeDelivery = (state) =>
  state.articleListing?.selectedScopeDelivery;

export const selectedPriceProposal = (state) =>
  state.articleListing?.priceProposal;
export const selectedStartTime = (state) => state.articleListing?.startTime;
export const selectedEbayTitle = (state) => state.articleListing?.ebayTitle;
export const selectProductQuantity = (state) =>
  state.articleListing?.productQuantity;
export const selectShippingCost = (state) => state.articleListing?.shippingCost;
export const selectHandlingType = (state) => state.articleListing?.handlingType;
export const selectProcessingTime = (state) =>
  state.articleListing?.processingTime;
export const selectMarketPickup = (state) => state.articleListing?.marketPickup;
export const selectEanNumber = (state) => state.articleListing?.eanNumber;
export const selectedCurrentView = (state) => state.articleListing?.currentView;
export const selectBreadcrumbItems = (state) =>
  state.articleListing?.breadcrumbItems;
export const selectedEbayCondition = (state) =>
  state.articleListing?.selectedEbayCondition;

export const selectedDiscountProfile = (state) =>
  state.articleListing?.selectedDiscountProfile;

export const selectedDiscountPercentage = (state) =>
  state.articleListing?.selectedDiscountPercentage;

export const selectEbayAspectData = (state) =>
  state.articleListing?.ebayAspectData;
export const getPersistedConditionDescription = (state) =>
  state?.articleListing?.persistedConditionDescription;
export const selectIsEbayListingAllowed = (state) =>
  state.articleListing?.isEbayListingAllowed;
export const selectIsMiraklListingAllowed = (state) =>
  state.articleListing?.isMiraklListingAllowed;
export const selectCustomAssets = (state) => state.articleListing?.customAssets;
export const selectCustomAssetsProductId = (state) => state.articleListing?.customAssetsProductId;
export const selectSelectedMiraklCondition = (state) =>
  state.articleListing?.selectedMiraklCondition;
export const selectSelectedMiraklDiscountProfile = (state) =>
  state.articleListing?.selectedMiraklDiscountProfile;
export const selectMiraklDiscountPercentage = (state) =>
  state.articleListing?.selectedMiraklDiscountPercentage;
// Export reducer
export default articleListingSlice.reducer;
